import { z } from 'zod'
import type { ColorScheme } from '@lib/enums/color-scheme.enum'

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const
export const themePreferenceSchema = z.enum(THEME_PREFERENCES)
export type ThemePreference = z.infer<typeof themePreferenceSchema>
/** What is actually showing. `system` has already been resolved. */
export type ResolvedTheme = ColorScheme

/** Shared with public/theme-init.js, which applies the theme before React loads. */
export const THEME_STORAGE_KEY = 'pixelsign.theme'

/**
 * Borrower-facing pages that always render light: signers sign white-paper
 * documents in the tenant's brand. Keep in sync with public/theme-init.js.
 */
export const LIGHT_ONLY_PATH_PREFIXES = ['/sign/', '/embed/', '/qa/'] as const

export function readStoredPreference(): ThemePreference {
  try {
    const parsed = themePreferenceSchema.safeParse(localStorage.getItem(THEME_STORAGE_KEY))
    return parsed.success ? parsed.data : 'system'
  } catch {
    return 'system'
  }
}

export function storePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page only.
  }
}

export function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}
